import { can, resolveModuleKey } from "./moduleAccess";

/**
 * Shared reference lookups used inside other modules' forms.
 * These MUST NOT require supplier_master / customer_master / user_management /
 * procurement_tracker screen access — only the parent module (or admin).
 *
 * ## Backend contract
 * Allow authenticated picker reads when the user has access to a dependent
 * parent module, without granting that entity's master module menu/CRUD.
 *
 * Examples:
 * - sales_quotes → get-all-customers, get-users, get-all-procurements (PR picker)
 * - sales_order → get-all-customers, get-users
 * - rfq / po → get-all-procurements, get-suppliers, get-users
 *
 * Full master list/create/update/delete APIs stay behind their own moduleAccess
 * (e.g. procurement_tracker screens still need procurement_tracker view).
 * Responses from lookup list APIs must NOT include permissions/moduleAccess
 * that expand the caller's role (frontend also ignores those shapes).
 */

export const LOOKUP_KINDS = ["supplier", "customer", "user", "procurement"];

/** API actions treated as lookups (not master-module screen access). */
export const LOOKUP_API_ACTIONS = new Set([
  "get-suppliers",
  "get-all-suppliers",
  "get-supplier",
  "get-active-suppliers",
  "get-all-customers",
  "get-customers",
  "get-customer",
  "get-customer-by-id",
  "get-users",
  "get-user",
  "get-rfq-users",
  "get-sales-quotes-users",
  "get-sales-order-users",
  "get-po-users",
  "get-price-users",
  // Purchase Request pickers (e.g. Sales Quotes Materials → Search by PR Number)
  "get-all-procurements",
  "get-procurements-by-prno",
  "get-procurement",
]);

/**
 * Parent module → which lookup kinds its forms may call.
 * Keep in sync when adding new create/edit screens that pick references.
 */
const PARENT_LOOKUP_MAP = {
  procurement_tracker: ["supplier", "customer", "user"],
  rfq: ["supplier", "customer", "user", "procurement"],
  po: ["supplier", "user", "procurement"],
  price_tracker: ["supplier", "user"],
  sales_quotes: ["customer", "user", "procurement"],
  sales_order: ["customer", "user"],
  sales_tracker: ["customer", "user"],
  sales_approval: ["customer", "user"],
  ecatalogue: ["customer", "user"],
  goods_outwards: ["customer", "user"],
  goods_inwards: ["supplier", "user"],
  payment_tracker: ["customer", "user"],
  tracker_update: ["supplier", "customer", "user"],
  task_management: ["user"],
  inventory_management: ["user"],
  // Master screens may also look up users for filters
  supplier_master: ["user"],
  customer_master: ["user"],
  user_management: ["user"],
};

/**
 * @param {string} parentModule - e.g. "sales_quotes", "procurement_tracker"
 * @returns {string[]} lookup kinds
 */
export function getLookupsForModule(parentModule) {
  const key = resolveModuleKey(parentModule);
  return PARENT_LOOKUP_MAP[key] || PARENT_LOOKUP_MAP[parentModule] || [];
}

/**
 * True if the user may call a shared lookup from inside a parent module form.
 * Does NOT grant sidebar/route access to the looked-up module's screens.
 *
 * @param {object|null} user - Redux user / access blob
 * @param {'supplier'|'customer'|'user'|'procurement'} lookupKind
 * @param {string} parentModule - module whose form is open
 * @param {'view'|'create'|'update'} [parentAction='view']
 */
export function canLookup(
  user,
  lookupKind,
  parentModule,
  parentAction = "view",
) {
  const kind = String(lookupKind || "")
    .trim()
    .toLowerCase();
  if (!LOOKUP_KINDS.includes(kind)) return false;

  // Full master module view also allows that kind of lookup
  if (kind === "supplier" && can(user, "supplier_master", "view")) return true;
  if (kind === "customer" && can(user, "customer_master", "view")) return true;
  if (kind === "user" && can(user, "user_management", "view")) return true;
  if (kind === "procurement" && can(user, "procurement_tracker", "view")) {
    return true;
  }

  const allowedKinds = getLookupsForModule(parentModule);
  if (!allowedKinds.includes(kind)) return false;

  // Parent module must be usable (view / create / update)
  return (
    can(user, parentModule, parentAction) ||
    can(user, parentModule, "view") ||
    can(user, parentModule, "create") ||
    can(user, parentModule, "update")
  );
}

/**
 * Map a backend action name → lookup kind, or null if not a lookup action.
 */
export function lookupKindFromAction(action) {
  const a = String(action || "")
    .trim()
    .toLowerCase();
  if (!LOOKUP_API_ACTIONS.has(a)) return null;
  if (a.includes("supplier")) return "supplier";
  if (a.includes("customer")) return "customer";
  if (a.includes("user")) return "user";
  if (a.includes("procurement")) return "procurement";
  return null;
}

/**
 * Helper for form screens: should we attempt this lookup API?
 * Prefer calling with the parent module of the current screen.
 */
export function canCallLookupAction(user, action, parentModule, parentAction) {
  const kind = lookupKindFromAction(action);
  if (!kind) return true; // not a lookup — leave to normal module gates
  return canLookup(user, kind, parentModule, parentAction);
}
